import { db } from '@/db/dexieClient';
import { uow } from '@/db/unitOfWork';
import { deviceImageEndpoints, syncEndpoints } from '@/services/api/apiConfig';
import { DEVICES_IMAGES_CACHE_NAME } from '@/domains/deviceImages/repositories/deviceImageRepository';
import { apiRequest } from '@/composables/useApi';
import { useDateFormat } from '@/composables/useDateFormat';
import { getUserFromCache } from '@/domains/auth/repositories/userRepository';
import { useAuthStore } from '@/domains/auth/stores/useAuthStore';
import {
  addSyncJob,
  getPendingAndFailedJobs,
  markAsSyncing,
  markAsSynced,
  markAsFailed,
  getFailedJobsExceedingThreshold,
  moveToDeadLetter,
  getSyncStatusCounts,
} from './repositories/syncQueueRepository';
import type { SyncMessageDto } from './types/SyncMessageDto';

export const SyncJobTypes = {
  BROKER_ROUTE_UPDATE: 'BROKER_ROUTE_UPDATE',
  IMAGE_CREATE: 'IMAGE_CREATE',
  IMAGE_DELETE: 'IMAGE_DELETE',
  ROUTE_STOP_UPDATE: 'ROUTE_STOP_UPDATE',
  ROUTE_UPDATE: 'ROUTE_UPDATE',
  SHIPMENT_UPDATE: 'SHIPMENT_UPDATE',
} as const;

export type SyncJobType = (typeof SyncJobTypes)[keyof typeof SyncJobTypes];

export interface SyncJob {
  id: string;
  type: SyncJobType;
  newRecord: unknown;
  oldRecord: unknown;
  status: 'pending' | 'syncing' | 'synced' | 'failed' | 'dead-letter';
  createdAt: Date;
  syncedAt?: Date;
  retryCount: number;
  error?: string;
}

interface SyncConfig {
  maxRetryAttempts: number;
  baseDelayMs: number;
  maxDelayMs: number;
  syncIntervalMs: number;
  deadLetterThreshold: number;
}

type ImageUploadResult = {
  success: boolean;
  imageId: number;
};

type ImageDeleteResult = {
  success: boolean;
};

class SyncService {
  private isOnline = navigator.onLine;
  private isSyncing = false;
  private syncInterval: ReturnType<typeof setInterval> | null = null;
  private config: SyncConfig = {
    maxRetryAttempts: 3,
    baseDelayMs: 1000,
    maxDelayMs: 30000,
    syncIntervalMs: 5 * 60 * 1000, // 5 minutes
    deadLetterThreshold: 5, // Move to dead letter after 5 failed attempts
  };

  constructor(config?: Partial<SyncConfig>) {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    this.setupEventListeners();
  }

  private setupEventListeners() {
    globalThis.addEventListener('online', () => {
      this.isOnline = true;
      this.triggerSync();
    });

    globalThis.addEventListener('offline', () => {
      this.isOnline = false;
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.isOnline) {
        this.triggerSync();
      }
    });
  }

  startPeriodicSync(intervalMs?: number) {
    if (this.syncInterval) return;

    const interval = intervalMs ?? this.config.syncIntervalMs;
    this.syncInterval = globalThis.setInterval(() => {
      if (this.isOnline) {
        this.triggerSync();
      }
    }, interval);
  }

  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async enqueue(job: Omit<SyncJob, 'id' | 'createdAt' | 'status' | 'retryCount'>): Promise<void> {
    const { actions: dateActions } = useDateFormat();
    const syncJob: SyncJob = {
      ...job,
      id: crypto.randomUUID(),
      status: 'pending',
      createdAt: dateActions.utcNowDate(),
      retryCount: 0,
    };

    for (let attempt = 0; attempt < this.config.maxRetryAttempts; attempt++) {
      try {
        await addSyncJob(syncJob);
        if (this.isOnline) {
          this.triggerSync();
        }
        return;
      } catch (error) {
        const isLastAttempt = attempt === this.config.maxRetryAttempts - 1;
        this.logEnqueueError(syncJob, error, attempt + 1);

        if (isLastAttempt) {
          console.warn('[SyncService] Failed to enqueue sync job after all retry attempts:', {
            jobType: syncJob.type,
            jobId: syncJob.id,
            attempts: this.config.maxRetryAttempts,
            error: error instanceof Error ? error.message : String(error),
          });
          throw this.createEnqueueError(error);
        }

        console.warn(
          `[SyncService] Retrying enqueue (attempt ${attempt + 1}/${this.config.maxRetryAttempts}) for job type: ${syncJob.type}`
        );
        await this.waitForRetry(attempt);
      }
    }
  }

  private logEnqueueError(syncJob: SyncJob, error: unknown, attempt: number): void {
    console.error(
      `[SyncService] Failed to enqueue sync job (attempt ${attempt}/${this.config.maxRetryAttempts}):`,
      {
        syncJob: {
          id: syncJob.id,
          type: syncJob.type,
          status: syncJob.status,
          createdAt: syncJob.createdAt,
        },
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      }
    );
  }

  private createEnqueueError(error: unknown): Error {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const enhancedError = new Error(
      `Failed to enqueue sync job after ${this.config.maxRetryAttempts} attempts: ${errorMessage}`
    );
    if (error instanceof Error && error.stack) {
      enhancedError.stack = error.stack;
    }
    return enhancedError;
  }

  private async waitForRetry(attempt: number): Promise<void> {
    const delayMs = Math.min(
      this.config.baseDelayMs * Math.pow(2, attempt) + Math.random() * 1000,
      this.config.maxDelayMs
    );
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  async triggerSync() {
    if (this.isSyncing) {
      console.warn('[SyncService] Sync already in progress, skipping trigger');
      return;
    }
    if (!this.isOnline) {
      console.warn('[SyncService] Device is offline, cannot sync');
      return;
    }

    this.isSyncing = true;

    try {
      await this.syncPendingJobs();
    } catch (error) {
      console.error('[SyncService] Sync failed:', error);
      console.warn('[SyncService] Sync process encountered an error:', {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
    } finally {
      this.isSyncing = false;
    }
  }

  private async syncPendingJobs() {
    const pendingJobs = await getPendingAndFailedJobs(this.config.deadLetterThreshold);

    if (pendingJobs.length === 0) {
      console.info('[SyncService] No pending or failed jobs to sync');
      return;
    }

    const failedJobsCount = pendingJobs.filter((job) => job.status === 'failed').length;

    if (failedJobsCount > 0) {
      console.warn(
        `[SyncService] Found ${failedJobsCount} failed jobs to retry out of ${pendingJobs.length} total jobs`
      );
    }

    console.info(`[SyncService] Syncing ${pendingJobs.length} jobs...`);

    const jobsByType = this.groupJobsByType(pendingJobs);

    for (const [type, jobs] of Object.entries(jobsByType)) {
      // Yield to the event loop between batches to prevent blocking
      await new Promise<void>((resolve) => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => resolve(), { timeout: 0 });
        } else {
          setTimeout(() => resolve(), 0);
        }
      });

      await this.syncBatch(type as SyncJobType, jobs);
    }

    await this.moveToDeadLetterQueue();
  }

  private groupJobsByType(jobs: SyncJob[]): Record<SyncJobType, SyncJob[]> {
    return jobs.reduce(
      (acc, job) => {
        acc[job.type] ??= [];
        acc[job.type].push(job);
        return acc;
      },
      {} as Record<SyncJobType, SyncJob[]>
    );
  }

  private async handleImageUpload(job: SyncJob): Promise<ImageUploadResult> {
    const payload = job.newRecord as {
      imageId: number;
      cacheKey: string;
      entityId: number;
      deviceImageTypeId: number;
      routeId?: number | null;
      routeStopId?: number | null;
      shipmentGuid?: string | null;
    };
    if (!payload.cacheKey || !payload.imageId) {
      console.warn('[SyncService] Image upload job has invalid payload:', {
        jobId: job.id,
        payload: {
          cacheKey: payload.cacheKey,
          imageId: payload.imageId,
          entityId: payload.entityId,
        },
      });
      throw new Error(`Job ${job.id} has invalid payload.`);
    }

    const cache = await caches.open(DEVICES_IMAGES_CACHE_NAME);
    const response = await cache.match(payload.cacheKey);

    if (!response) {
      console.warn('[SyncService] Image blob not found in cache:', {
        jobId: job.id,
        cacheKey: payload.cacheKey,
        imageId: payload.imageId,
        entityId: payload.entityId,
      });
      throw new Error(`Blob not found in cache for cacheKey: ${payload.cacheKey}`);
    }

    const blob = await response.blob();

    const formData = new FormData();
    const filename = `fallback_${payload.entityId}_${Date.now()}.jpg`;
    formData.append('image', blob, filename);

    try {
      const endpoint = deviceImageEndpoints.UPLOAD(payload.deviceImageTypeId, payload.entityId);
      await apiRequest({
        endpoint,
        method: 'post',
        body: formData,
      });

      return { success: true, imageId: payload.imageId };
    } catch (error) {
      console.error(`[SyncService] (Fallback) Upload failed for job ${job.id}:`, error);
      console.warn('[SyncService] Image upload API request failed:', {
        jobId: job.id,
        endpoint: deviceImageEndpoints.UPLOAD(payload.deviceImageTypeId, payload.entityId),
        imageId: payload.imageId,
        entityId: payload.entityId,
        deviceImageTypeId: payload.deviceImageTypeId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error; // Throw so syncBatch marks it as 'failed'
    }
  }

  private async handleImageDelete(job: SyncJob): Promise<ImageDeleteResult> {
    const payload = job.newRecord as { imageId: number };
    if (!payload.imageId || payload.imageId <= 0) {
      throw new Error(`Job ${job.id} has invalid image delete payload.`);
    }

    await apiRequest({
      endpoint: deviceImageEndpoints.DELETE(payload.imageId),
      method: 'delete',
    });

    return { success: true };
  }

  private getEntityName(type: SyncJobType): string {
    switch (type) {
      case SyncJobTypes.SHIPMENT_UPDATE:
        return 'Shipment';
      case SyncJobTypes.ROUTE_UPDATE:
        return 'Route';
      case SyncJobTypes.ROUTE_STOP_UPDATE:
        return 'RouteStop';
      case SyncJobTypes.BROKER_ROUTE_UPDATE:
        return 'BrokerRoute';
      default:
        throw new Error(`Unsupported batch sync type: ${type}`);
    }
  }

  private async handleImageUploadBatch(jobs: SyncJob[]): Promise<void> {
    const { actions: dateActions } = useDateFormat();

    for (const job of jobs) {
      if (!job) continue;
      try {
        await markAsSyncing(job.id);
        console.info(`[SyncService] Syncing job ${job.id} of type IMAGE_UPLOAD`);

        const uploadResult = await this.handleImageUpload(job);

        const syncedAt = dateActions.utcNowDate();
        await markAsSynced(job.id, syncedAt);

        if (uploadResult?.success) {
          const payload = job.newRecord as { imageId: number; cacheKey: string };

          await uow.runRW([db.deviceImages], async () => {
            await db.deviceImages.delete(payload.imageId);
          });

          const cache = await caches.open(DEVICES_IMAGES_CACHE_NAME);
          await cache.delete(payload.cacheKey);
        }
      } catch (error) {
        console.error(`[SyncService] Failed to sync job ${job.id}:`, error);
        console.warn('[SyncService] Image upload job failed:', {
          jobId: job.id,
          jobType: job.type,
          retryCount: job.retryCount + 1,
          error: error instanceof Error ? error.message : String(error),
          payload: {
            imageId: (job.newRecord as { imageId: number }).imageId,
            entityId: (job.newRecord as { entityId: number }).entityId,
          },
        });
        await markAsFailed(job.id, job.retryCount + 1, (error as Error).message);
      }
    }
  }

  private async handleImageDeleteBatch(jobs: SyncJob[]): Promise<void> {
    const { actions: dateActions } = useDateFormat();

    for (const job of jobs) {
      if (!job) continue;
      try {
        await markAsSyncing(job.id);
        console.info(`[SyncService] Syncing job ${job.id} of type IMAGE_DELETE`);

        await this.handleImageDelete(job);

        const syncedAt = dateActions.utcNowDate();
        await markAsSynced(job.id, syncedAt);
      } catch (error) {
        console.error(`[SyncService] Failed to sync job ${job.id}:`, error);
        await markAsFailed(job.id, job.retryCount + 1, (error as Error).message);
      }
    }
  }

  private async handleBatchSync(type: SyncJobType, jobs: SyncJob[]): Promise<void> {
    const { actions: dateActions } = useDateFormat();

    for (const job of jobs) {
      await markAsSyncing(job.id);
    }

    console.info(`[SyncService] Syncing batch of ${jobs.length} jobs of type ${type}`);

    try {
      // Try to get user from auth store first (active state), then fall back to cache
      const authStore = useAuthStore();
      let user = authStore.state.user;
      user ??= await getUserFromCache();
      if (!user?.userAccountId) {
        console.warn('[SyncService] User not found or userAccountId missing during batch sync:', {
          jobType: type,
          jobCount: jobs.length,
          jobIds: jobs.map((j) => j.id),
        });
        throw new Error(
          'User not found or userAccountId missing. Cannot sync without user context.'
        );
      }

      let endpoint: string;
      const entityName = this.getEntityName(type);
      switch (type) {
        case SyncJobTypes.SHIPMENT_UPDATE:
          endpoint = syncEndpoints.SHIPMENTS();
          break;
        case SyncJobTypes.ROUTE_UPDATE:
          endpoint = syncEndpoints.ROUTES();
          break;
        case SyncJobTypes.ROUTE_STOP_UPDATE:
          endpoint = syncEndpoints.ROUTE_STOPS();
          break;
        case SyncJobTypes.BROKER_ROUTE_UPDATE:
          endpoint = syncEndpoints.BROKER_ROUTES();
          break;
        default:
          throw new Error(`Unsupported batch sync type: ${type}`);
      }

      const batchData: SyncMessageDto[] = jobs.map((job) => {
        // Ensure syncedOn is null in newRecord before sending
        const newRecord = {
          ...(job.newRecord as Record<string, unknown>),
          syncedOn: null,
        };

        const oldRecord = job.oldRecord || null;

        return {
          jobId: job.id,
          entity: entityName,
          operation: 'UPDATE',
          userAccountId: user.userAccountId,
          newRecord: newRecord,
          oldRecord: oldRecord,
        };
      });

      try {
        await apiRequest({
          endpoint,
          method: 'post',
          body: batchData,
        });
      } catch (apiError) {
        console.warn('[SyncService] API request failed during batch sync:', {
          endpoint,
          jobType: type,
          jobCount: jobs.length,
          error: apiError instanceof Error ? apiError.message : String(apiError),
        });
        throw apiError;
      }

      const syncedAt = dateActions.utcNowDate();
      for (const job of jobs) {
        await markAsSynced(job.id, syncedAt);
      }

      console.info(
        `[SyncService] Successfully synced batch of ${jobs.length} jobs of type ${type}`
      );
    } catch (error) {
      console.error(
        `[SyncService] Failed to sync batch of ${jobs.length} jobs of type ${type}:`,
        error
      );
      console.warn('[SyncService] Batch sync failed, marking all jobs as failed:', {
        jobType: type,
        jobCount: jobs.length,
        jobIds: jobs.map((j) => j.id),
        error: error instanceof Error ? error.message : String(error),
        retryCounts: jobs.map((j) => j.retryCount + 1),
      });

      // Failure: mark all jobs as failed
      const errorMessage = error instanceof Error ? error.message : String(error);
      for (const job of jobs) {
        await markAsFailed(job.id, job.retryCount + 1, errorMessage);
      }
    }
  }

  private async syncBatch(type: SyncJobType, jobs: SyncJob[]) {
    if (type === SyncJobTypes.IMAGE_CREATE) {
      await this.handleImageUploadBatch(jobs);
      return;
    }
    if (type === SyncJobTypes.IMAGE_DELETE) {
      await this.handleImageDeleteBatch(jobs);
      return;
    }
    if (
      type === SyncJobTypes.SHIPMENT_UPDATE ||
      type === SyncJobTypes.ROUTE_UPDATE ||
      type === SyncJobTypes.ROUTE_STOP_UPDATE ||
      type === SyncJobTypes.BROKER_ROUTE_UPDATE
    ) {
      await this.handleBatchSync(type, jobs);
      return;
    }

    console.warn(`[SyncService] Unknown sync job type: ${type}, skipping batch`, {
      jobType: type,
      jobCount: jobs.length,
      jobIds: jobs.map((j) => j.id),
    });
  }

  private async moveToDeadLetterQueue() {
    const deadLetterJobs = await getFailedJobsExceedingThreshold(this.config.deadLetterThreshold);

    if (deadLetterJobs.length === 0) {
      return;
    }

    console.warn(`[SyncService] Moving ${deadLetterJobs.length} jobs to dead letter queue`, {
      threshold: this.config.deadLetterThreshold,
      jobs: deadLetterJobs.map((job) => ({
        id: job.id,
        type: job.type,
        retryCount: job.retryCount,
        error: job.error,
        createdAt: job.createdAt,
      })),
    });

    for (let i = 0; i < deadLetterJobs.length; i++) {
      const job = deadLetterJobs[i];
      if (!job) continue;
      await moveToDeadLetter(job.id, job.retryCount);
    }
  }

  async getSyncStatus() {
    const { pendingCount, failedCount, deadLetterCount } = await getSyncStatusCounts();

    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      pendingCount,
      failedCount,
      deadLetterCount,
    };
  }

  getConfig(): SyncConfig {
    return { ...this.config };
  }

  updateConfig(newConfig: Partial<SyncConfig>) {
    this.config = { ...this.config, ...newConfig };
  }
}

export const syncService = new SyncService();
